import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import stockService, { Stock } from '@/services/stockService';

interface EditStockThresholdsModalProps {
  isOpen: boolean;
  onClose: () => void;
  stock: Stock | null;
  onSuccess: () => void;
}

const EditStockThresholdsModal: React.FC<EditStockThresholdsModalProps> = ({
  isOpen,
  onClose,
  stock,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    stock_minimum: '',
    stock_maximum: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (stock && isOpen) {
      setFormData({
        stock_minimum: stock.stock_minimum?.toString() || '',
        stock_maximum: stock.stock_maximum?.toString() || ''
      });
      setError(null);
    }
  }, [stock, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!stock) return;

    // Validation
    const stockMin = formData.stock_minimum ? parseFloat(formData.stock_minimum) : null;
    const stockMax = formData.stock_maximum ? parseFloat(formData.stock_maximum) : null;

    if (stockMin !== null && stockMin < 0) {
      setError('Le stock minimum ne peut pas être négatif');
      return;
    }

    if (stockMax !== null && stockMax < 0) {
      setError('Le stock maximum ne peut pas être négatif');
      return;
    }

    if (stockMin !== null && stockMax !== null && stockMin > stockMax) {
      setError('Le stock minimum ne peut pas être supérieur au stock maximum');
      return;
    }

    setIsLoading(true);
    try {
      // Préparer les données à envoyer (convertir null en chaîne vide pour permettre la suppression)
      const updateData: any = {};
      if (stockMin !== null && stockMin !== undefined) {
        updateData.stock_minimum = stockMin.toString();
      } else {
        updateData.stock_minimum = null; // Permettre la suppression du seuil
      }
      if (stockMax !== null && stockMax !== undefined) {
        updateData.stock_maximum = stockMax.toString();
      } else {
        updateData.stock_maximum = null; // Permettre la suppression du seuil
      }
      
      await stockService.updateStock(stock.id, updateData);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour des seuils:', err);
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour des seuils');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !stock) return null;

  const stockAny = stock as any;
  const productName = stockAny.produit_nom || stock.produit_details?.nom || 'Produit inconnu';
  const magasinName = stockAny.magasin_nom || stock.magasin_details?.nom || 'Magasin inconnu';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Modifier les seuils de stock
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informations du stock */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm">
              <div className="font-medium text-gray-900 mb-1">Produit</div>
              <div className="text-gray-600">{productName}</div>
            </div>
            <div className="text-sm mt-2">
              <div className="font-medium text-gray-900 mb-1">Magasin</div>
              <div className="text-gray-600">{magasinName}</div>
            </div>
            <div className="text-sm mt-2">
              <div className="font-medium text-gray-900 mb-1">Stock actuel</div>
              <div className="text-gray-600">{stock.stock_actuel.toLocaleString()} kg</div>
            </div>
          </div>

          {/* Champs de formulaire */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stock minimum (kg)
                <span className="text-gray-400 text-xs font-normal ml-2">(optionnel)</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.stock_minimum}
                onChange={(e) => setFormData({ ...formData, stock_minimum: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Ex: 100"
              />
              <p className="mt-1 text-xs text-gray-500">
                Seuil d'alerte en dessous duquel le stock est considéré comme critique
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stock maximum (kg)
                <span className="text-gray-400 text-xs font-normal ml-2">(optionnel)</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.stock_maximum}
                onChange={(e) => setFormData({ ...formData, stock_maximum: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Ex: 5000"
              />
              <p className="mt-1 text-xs text-gray-500">
                Capacité maximale de stockage pour ce produit dans ce magasin
              </p>
            </div>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStockThresholdsModal;

